/**
 * Target Match Conditions for Capability Filtering
 *
 * This module defines match conditions that determine which targets are valid
 * for specific capabilities. Until the backend exposes these conditions via API,
 * we maintain them here for frontend filtering.
 *
 * Match conditions are derived from Janus YAML capability definitions.
 *
 * @see /modules/janus/templates/*.yml for source of truth
 * @temporary This is a temporary workaround (~3 months) until backend API exposes match conditions
 * @removal Expected removal: Q1 2026 when AgoraCapability includes match field
 *
 * ## Removal Guide
 * When backend API exposes match conditions in AgoraCapability:
 * 1. Add `match: AgoraMatchCondition[]` field to Capability interface in useCapabilities.ts
 * 2. Update TargetSelector.tsx to use `capability.match` instead of getCapabilityMatchConfig()
 * 3. Update buildTargetFilters() to accept AgoraMatchCondition[] instead of CapabilityTargetConfig
 * 4. Delete this entire file and its test file
 * 5. Remove imports of this module from TargetSelector.tsx
 */

import { QueryFilter } from '@/types';

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Valid target types that capabilities can operate on.
 */
export type TargetType =
  | 'port'
  | 'asset'
  | 'attribute'
  | 'addomain'
  | 'seed'
  | 'preseed'
  | 'webpage'
  | 'repository';

/**
 * Match condition types using discriminated unions for type safety.
 * These mirror the match condition patterns in Janus YAML templates.
 */
export type MatchCondition =
  | PortMatchCondition
  | ServiceMatchCondition
  | AssetClassMatchCondition
  | FieldValueMatchCondition
  | RegexMatchCondition;

/**
 * Match ports by port number.
 * Backend: `is: 1883` where value is a number
 */
export interface PortMatchCondition {
  type: 'port';
  /** List of valid port numbers (OR logic - any match is valid) */
  ports: readonly number[];
}

/**
 * Match ports by service name prefix.
 * Backend: `is: http` where value is a service name
 */
export interface ServiceMatchCondition {
  type: 'service';
  /** List of valid service name patterns (OR logic - any match is valid) */
  services: readonly string[];
}

/**
 * Match assets by asset class.
 * Backend: `is: domain`, `is: tld`, `is: ipv4`, etc.
 */
export interface AssetClassMatchCondition {
  type: 'asset-class';
  /** List of valid asset classes */
  classes: readonly (
    | 'domain'
    | 'tld'
    | 'ipv4'
    | 'ipv6'
    | 'cidr'
    | 'aws'
    | 's3'
  )[];
}

/**
 * Match targets by exact field value.
 * Backend: `name: service`, `value: ftp`
 */
export interface FieldValueMatchCondition {
  type: 'field-value';
  /** Field name to match against */
  field: string;
  /** Exact values to match (OR logic) */
  values: readonly string[];
}

/**
 * Match targets by regex pattern on a field.
 * Backend: `name: url`, `regex: "github.com"`
 */
export interface RegexMatchCondition {
  type: 'regex';
  /** Field name to match against */
  field: string;
  /** Regex pattern to match */
  pattern: string;
}

/**
 * Configuration for a capability's target requirements.
 */
export interface CapabilityTargetConfig {
  /** Target type this config applies to */
  target: TargetType;
  /** Match conditions to filter targets */
  conditions: readonly MatchCondition[];
  /**
   * How to combine multiple conditions:
   * - 'or': Any condition match is sufficient (default for Janus templates)
   * - 'and': All conditions must match
   */
  combinator: 'and' | 'or';
}

// =============================================================================
// Capability Match Configurations
// =============================================================================

/**
 * Capability target match configurations.
 *
 * These mirror the `input.match` conditions defined in Janus YAML templates.
 * Generated from: /modules/janus/templates/*.yml
 *
 * Match Logic (from backend):
 * - Multiple conditions use OR logic at top level
 * - Within single condition: is + name/value use AND logic
 * - Empty match array = match all targets
 */
export const CAPABILITY_TARGET_MATCHES: Record<string, CapabilityTargetConfig> =
  {
    // =========================================================================
    // Asset-based capabilities (target: asset)
    // =========================================================================

    /** Checks for DNS misconfigurations on domains */
    'bad-dns': {
      target: 'asset',
      conditions: [{ type: 'asset-class', classes: ['domain', 'tld'] }],
      combinator: 'or',
    },

    /** Cloud storage bucket enumeration */
    cloudbrute: {
      target: 'asset',
      conditions: [{ type: 'asset-class', classes: ['tld'] }],
      combinator: 'or',
    },

    /** Email security configuration checker (SPF, DKIM, DMARC) */
    mailsecchk: {
      target: 'asset',
      conditions: [{ type: 'asset-class', classes: ['tld', 'domain'] }],
      combinator: 'or',
    },

    // =========================================================================
    // Port-based capabilities (target: port)
    // =========================================================================

    /** Anonymous FTP login check (field-value matching) */
    'anon-ftp': {
      target: 'port',
      conditions: [{ type: 'field-value', field: 'service', values: ['ftp'] }],
      combinator: 'or',
    },

    /** Anonymous FTP/FTPS login with SSL */
    'anon-ftps': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['ftp'] },
        { type: 'port', ports: [21, 990] },
      ],
      combinator: 'or',
    },

    /** Cisco Smart Install protocol vulnerability check */
    'cisco-smart-install': {
      target: 'port',
      conditions: [{ type: 'port', ports: [4786] }],
      combinator: 'or',
    },

    /** HTTP cookie security scanner */
    'cookie-monster': {
      target: 'port',
      conditions: [{ type: 'service', services: ['http', 'https'] }],
      combinator: 'or',
    },

    /** CouchDB authentication check */
    'couchdb-login': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['couchdb'] },
        { type: 'port', ports: [5984] },
      ],
      combinator: 'or',
    },

    /** DNS zone transfer vulnerability check */
    'dns-zone-transfer': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['dns'] },
        { type: 'port', ports: [53] },
      ],
      combinator: 'or',
    },

    /** Finger protocol user enumeration */
    'finger-username-enumeration': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['finger'] },
        { type: 'port', ports: [79] },
      ],
      combinator: 'or',
    },

    /** IMAP NTLM information disclosure */
    'imap-ntlm-info': {
      target: 'port',
      conditions: [{ type: 'service', services: ['imap'] }],
      combinator: 'or',
    },

    /** IPMI cipher zero vulnerability check */
    'ipmi-cipher-zero': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['ipmi'] },
        { type: 'port', ports: [623] },
      ],
      combinator: 'or',
    },

    /** IPMI password hash extraction */
    'ipmi-hash-extraction': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['ipmi'] },
        { type: 'port', ports: [623] },
      ],
      combinator: 'or',
    },

    /** Java Debug Wire Protocol enumeration */
    'java-jdwp-enum': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['jdwp'] },
        { type: 'port', ports: [5005, 5000, 8000, 8787, 9009] },
      ],
      combinator: 'or',
    },

    /** Java JMX RMI scanner */
    'java-jmx-scanner': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['rmi', 'jmx'] },
        { type: 'port', ports: [1099, 8686, 8880, 9999] },
      ],
      combinator: 'or',
    },

    /** Java RMI registry scanner */
    'java-rmi-scanner': {
      target: 'port',
      conditions: [
        { type: 'port', ports: [1099, 8686, 8880] },
        { type: 'service', services: ['rmi', 'java-rmi'] },
      ],
      combinator: 'or',
    },

    /** Java RMI scanner (NSE variant) */
    'java-rmi-scanner-nse': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['rmi'] },
        { type: 'port', ports: [1099] },
      ],
      combinator: 'or',
    },

    /** LDAP null bind check */
    'ldap-null-bind': {
      target: 'port',
      conditions: [{ type: 'service', services: ['ldap'] }],
      combinator: 'or',
    },

    /** MongoDB authentication check */
    'mongodb-login': {
      target: 'port',
      conditions: [{ type: 'port', ports: [27017] }],
      combinator: 'or',
    },

    /** Microsoft SQL Server login brute force */
    'mssql-login': {
      target: 'port',
      conditions: [{ type: 'service', services: ['mssql'] }],
      combinator: 'or',
    },

    /** MySQL login brute force */
    'mysql-login': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['mysql'] },
        { type: 'port', ports: [3306] },
      ],
      combinator: 'or',
    },

    /** NFS share enumeration */
    'nfs-share': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['nfs'] },
        { type: 'port', ports: [2049] },
      ],
      combinator: 'or',
    },

    /** Open X11 display check */
    'open-x11': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['x11'] },
        { type: 'port', ports: [6000] },
      ],
      combinator: 'or',
    },

    /** Oracle database login brute force */
    'oracle-database-login': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['oracledb'] },
        { type: 'port', ports: [1521] },
      ],
      combinator: 'or',
    },

    /** PostgreSQL login brute force */
    'postgres-login': {
      target: 'port',
      conditions: [{ type: 'service', services: ['postgresql'] }],
      combinator: 'or',
    },

    /** Redis authentication check */
    'redis-login': {
      target: 'port',
      conditions: [{ type: 'service', services: ['redis'] }],
      combinator: 'or',
    },

    /** CVE-2025-53770 vulnerability scanner */
    'scanner-cve-2025-53770': {
      target: 'port',
      conditions: [{ type: 'service', services: ['http', 'https'] }],
      combinator: 'or',
    },

    /** SMB user enumeration */
    'smb-userenum': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['smb'] },
        { type: 'port', ports: [445] },
      ],
      combinator: 'or',
    },

    /** HTTP request smuggling checker */
    smugchunks: {
      target: 'port',
      conditions: [{ type: 'service', services: ['http', 'https'] }],
      combinator: 'or',
    },

    /** SNMP community string brute force */
    'snmp-community-strings': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['snmp'] },
        { type: 'port', ports: [161, 162] },
      ],
      combinator: 'or',
    },

    /** Telnet login brute force */
    'telnet-login': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['telnet'] },
        { type: 'port', ports: [23] },
      ],
      combinator: 'or',
    },

    /** Unauthenticated MQTT broker check */
    'unauth-mqtt-broker': {
      target: 'port',
      conditions: [
        { type: 'service', services: ['mqtt'] },
        { type: 'port', ports: [1883] },
      ],
      combinator: 'or',
    },

    // =========================================================================
    // Repository-based capabilities (target: repository)
    // =========================================================================

    /** npm/PyPI dependency confusion scanner */
    'dependency-confusion-scanner': {
      target: 'repository',
      conditions: [{ type: 'regex', field: 'url', pattern: 'github.com' }],
      combinator: 'or',
    },

    /** GitHub Actions artifact poisoning scanner */
    'gato-artifacts': {
      target: 'repository',
      conditions: [{ type: 'regex', field: 'url', pattern: 'github.com' }],
      combinator: 'or',
    },

    // =========================================================================
    // Preseed-based capabilities (target: preseed)
    // =========================================================================

    /**
     * EDGAR subsidiary extraction - requires CIK (numeric company ID) preseeds.
     * Use collect-edgar first to discover CIKs from company names.
     */
    edgar: {
      target: 'preseed',
      conditions: [
        {
          type: 'field-value',
          field: 'type',
          values: ['cik'] as const,
        },
      ],
      combinator: 'or',
    },

    /**
     * EDGAR CIK discovery - expects company name preseeds (edgar+company).
     * Discovers CIKs that can then be used with the edgar capability.
     */
    'collect-edgar': {
      target: 'preseed',
      conditions: [
        {
          type: 'field-value',
          field: 'type',
          values: ['edgar+company'] as const,
        },
      ],
      combinator: 'or',
    },
  } as const;

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Type guard to check if a capability has match conditions configured.
 */
export function hasMatchConfig(
  capabilityName: string
): capabilityName is keyof typeof CAPABILITY_TARGET_MATCHES {
  return capabilityName in CAPABILITY_TARGET_MATCHES;
}

/**
 * Get match configuration for a capability.
 * Returns undefined if no specific filtering is required.
 */
export function getCapabilityMatchConfig(
  capabilityName: string | undefined
): CapabilityTargetConfig | undefined {
  if (!capabilityName) return undefined;
  return CAPABILITY_TARGET_MATCHES[capabilityName];
}

/**
 * Type assertion helper for exhaustive switch statements.
 * Used by getMatchDescription which still uses switch-case pattern.
 */
function assertNever(x: never): never {
  throw new Error(`Unexpected match condition type: ${JSON.stringify(x)}`);
}

/**
 * Handler function type for converting a match condition to query filters.
 */
type ConditionHandler = (condition: MatchCondition) => QueryFilter[];

/**
 * Lookup table mapping condition types to their filter builders.
 * Using Record<MatchCondition['type'], ...> ensures TypeScript will error
 * if a new condition type is added without a corresponding handler.
 */
const conditionHandlers: Record<MatchCondition['type'], ConditionHandler> = {
  port: condition => {
    const c = condition as PortMatchCondition;
    return c.ports.map(port => ({
      field: 'port',
      operator: '=',
      value: port,
    }));
  },

  service: condition => {
    const c = condition as ServiceMatchCondition;
    return c.services.map(service => ({
      field: 'service',
      operator: 'CONTAINS',
      value: service,
    }));
  },

  'asset-class': condition => {
    const c = condition as AssetClassMatchCondition;
    return c.classes.map(cls => ({
      field: 'class',
      operator: '=',
      value: cls,
    }));
  },

  'field-value': condition => {
    const c = condition as FieldValueMatchCondition;
    return c.values.map(value => ({
      field: c.field,
      operator: '=',
      value,
    }));
  },

  regex: condition => {
    const c = condition as RegexMatchCondition;
    // Note: Full regex is not supported by GraphQuery, using CONTAINS
    return [{ field: c.field, operator: 'CONTAINS', value: c.pattern }];
  },
};

/**
 * Build GraphQuery filter conditions for target filtering.
 * Returns filter objects compatible with useMy GraphQuery.
 *
 * @param config - The capability's target requirements configuration
 * @returns Array of QueryFilter objects for useMy GraphQuery
 */
export function buildTargetFilters(
  config: CapabilityTargetConfig
): QueryFilter[] {
  const subFilters = config.conditions.flatMap(condition =>
    conditionHandlers[condition.type](condition)
  );

  if (subFilters.length === 0) {
    return [];
  }

  return [
    {
      field: '',
      operator: config.combinator === 'and' ? 'AND' : 'OR',
      value: subFilters,
    },
  ];
}

/**
 * Get a human-readable description of what targets are valid for a capability.
 * Used for empty state messaging in the UI.
 *
 * @param capabilityName - The capability name to get description for
 * @returns Human-readable description or undefined if no config exists
 */
export function getMatchDescription(
  capabilityName: string
): string | undefined {
  const config = getCapabilityMatchConfig(capabilityName);
  if (!config) return undefined;

  const descriptions: string[] = [];

  for (const condition of config.conditions) {
    switch (condition.type) {
      case 'port':
        descriptions.push(`ports ${condition.ports.join(', ')}`);
        break;
      case 'service':
        descriptions.push(
          `services containing "${condition.services.join('" or "')}"`
        );
        break;
      case 'asset-class':
        descriptions.push(`${condition.classes.join(' or ')} assets`);
        break;
      case 'field-value':
        descriptions.push(
          `${condition.field} equal to "${condition.values.join('" or "')}"`
        );
        break;
      case 'regex':
        descriptions.push(`${condition.field} matching "${condition.pattern}"`);
        break;
      default:
        assertNever(condition);
    }
  }

  if (descriptions.length === 0) return undefined;

  const connector = config.combinator === 'or' ? ' or ' : ' and ';
  return descriptions.join(connector);
}
